import React, { useCallback, useMemo } from "react";
import useAssistance from "../hooks/useAssistance";
import { useAlert, useChime } from "../hooks";
import { useTranslation } from "react-i18next";

export const ChimeMeetingContext = React.createContext({});

const ChimeMeetingProvider = ({ children }) => {
  const { t } = useTranslation();
  const { assistance } = useAssistance();
  const { alert } = useAlert();
  const userLeftAlertId = "user-left-alert";
  const userJoinedAlertId = "user-joined-alert";

  const meetingAttendee = useMemo(() => {
    const attendees = assistance?.attendees;

    return attendees?.find((attendee) => {
      const externalIds = attendee?.ExternalUserId?.split("@");

      if (externalIds?.[0] === "operator-web") {
        return true;
      }
      return false;
    });
  }, [assistance]);

  const userAttendeeId = useMemo(() => {
    const attendees = assistance?.attendees;

    return attendees?.find((attendee) => {
      const externalIds = attendee?.ExternalUserId?.split("@");

      if (externalIds?.[0] === "user") {
        return true;
      }
      return false;
    })?.AttendeeId;
  }, [assistance]);

  const onAttendeePresenceChange = useCallback(
    (attendeeId, present) => {
      if (!present && attendeeId === userAttendeeId) {
        alert({
          id: userLeftAlertId,
          title: t("plugin.toasts.driverLeft.title"),
          description: t("plugin.toasts.driverLeft.description"),
          status: "warning",
        });
      } else if (!present && attendeeId !== meetingAttendee?.AttendeeId) {
        alert({
          id: userLeftAlertId,
          title: t("plugin.toasts.attendeeLeft.title"),
          description: t("plugin.toasts.attendeeLeft.description"),
          status: "warning",
        });
      } else {
        if (present && userAttendeeId !== attendeeId && meetingAttendee?.AttendeeId !== attendeeId) {
          alert({
            id: userJoinedAlertId,
            title: t("plugin.toasts.attendeeJoined.title"),
            description: t("plugin.toasts.attendeeJoined.description"),
            status: "success",
          });
        }
      }
    },
    [alert, userAttendeeId, meetingAttendee?.AttendeeId, t],
  );

  const onNetworkError = useCallback(() => {
    if (!alert.isActive(userLeftAlertId)) {
      alert({
        title: t("plugin.toasts.networkError.title"),
        description: t("plugin.toasts.networkError.description"),
        status: "error",
      });
    }
  }, [alert, t]);

  const onPoorConnection = useCallback(() => {
    alert({
      title: t("plugin.toasts.poorConnection.title"),
      description: t("plugin.toasts.poorConnection.description"),
      status: "error",
    });
  }, [alert, t]);

  const onGoodConnection = useCallback(() => {
    alert({
      title: t("plugin.toasts.goodConnection.title"),
      description: t("plugin.toasts.goodConnection.description"),
      status: "success",
    });
  }, [alert, t]);

  const {
    hasJoined,
    joinMeeting,
    leaveMeeting,
    addMessagingObserver,
    removeMessagingObserver,
    listChannelMessages,
    getReadMarkerTimestamp,
    sendChannelMessage,
    updateReadMarkerTimestamp,
    muteLocalAudio,
    unMuteLocalAudio,
    audioVideo,
  } = useChime({
    meeting: assistance?.Meeting,
    meetingAttendee,
    messagingUserArn: assistance?.MessagingOperator,
    messagingChannelArn: assistance?.MessagingChannel,
    onAttendeePresenceChange,
    onGoodConnection,
    onNetworkError,
    onPoorConnection,
  });

  const listStandardMessages = useCallback(async () => {
    let standardMessages = [];
    try {
      let response = null;
      do {
        response = await listChannelMessages(response?.NextToken);

        if (response) {
          standardMessages = [
            ...standardMessages,
            ...response?.ChannelMessages?.filter(
              (message) => message.Type === "STANDARD",
            ),
          ];
        }
      } while (response?.NextToken);

      return standardMessages;
    } catch (error) {
      if (!alert.isActive(error.message)) {
        alert({
          description: error.message,
          id: error.message,
          status: "error",
          title: "Error",
        });
      }
    }
  }, [alert, listChannelMessages]);

  const sendControlMessage = useCallback(
    async (content) => {
      try {
        const response = await sendChannelMessage({
          content: content,
          persistence: "NON_PERSISTENT",
          type: "CONTROL",
        });

        return response;
      } catch (error) {
        if (!alert.isActive(error.message)) {
          alert({
            description: error.message,
            id: error.message,
            status: "error",
            title: "Error",
          });
        }
      }
    },
    [alert, sendChannelMessage],
  );

  const sendStandardMessage = useCallback(
    async (content) => {
      try {
        const response = await sendChannelMessage({
          content: content,
          persistence: "PERSISTENT",
          type: "STANDARD",
        });

        return response;
      } catch (error) {
        if (!alert.isActive(error.message)) {
          alert({
            description: error.message,
            id: error.message,
            status: "error",
            title: "Error",
          });
        }
      }
    },
    [alert, sendChannelMessage],
  );

  const getReadMarker = useCallback(async () => {
    try {
      return await getReadMarkerTimestamp();
    } catch (error) {
      if (!alert.isActive(error.message)) {
        alert({
          description: error.message,
          id: error.message,
          status: "error",
          title: "Error",
        });
      }
    }
  }, [alert, getReadMarkerTimestamp]);

  const updateReadMarker = useCallback(async () => {
    try {
      await updateReadMarkerTimestamp();
    } catch (error) {
      if (!alert.isActive(error.message)) {
        alert({
          description: error.message,
          id: error.message,
          status: "error",
          title: "Error",
        });
      }
    }
  }, [alert, updateReadMarkerTimestamp]);

  const contextValue = useMemo(
    () => ({
      hasJoined,
      joinMeeting,
      leaveMeeting,
      addMessagingObserver,
      removeMessagingObserver,
      listStandardMessages,
      sendControlMessage,
      sendStandardMessage,
      getReadMarker,
      updateReadMarker,
      muteLocalAudio,
      unMuteLocalAudio,
      audioVideo,
    }),
    [
      hasJoined,
      joinMeeting,
      leaveMeeting,
      addMessagingObserver,
      removeMessagingObserver,
      listStandardMessages,
      sendControlMessage,
      sendStandardMessage,
      getReadMarker,
      updateReadMarker,
      muteLocalAudio,
      unMuteLocalAudio,
      audioVideo,
    ],
  );

  return (
    <ChimeMeetingContext.Provider value={contextValue}>
      {children}
    </ChimeMeetingContext.Provider>
  );
};

export default React.memo(ChimeMeetingProvider);
